import Information20 from '@carbon/icons-react/es/information/20';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { confirm } from 'components/atoms/modal';
import { Switch } from 'components/atoms/switch/switch';
import { BodyShort01, Label01 } from 'components/styles';
import { TranslationKey, useTranslation } from 'hooks/use-translation';

const InfoIcon = styled(Information20)``;
const Message = styled.div`
  ${BodyShort01};
  display: flex;
  gap: var(--spacing-05);
  align-items: center;

  ${InfoIcon} {
    color: var(--warning-border-color);
  }
`;

const Control = styled.div`
  ${BodyShort01};
  display: flex;
  gap: var(--spacing-05);
  align-items: center;
`;

const Wrapper = styled.div`
  background-color: var(--warning-bg-color);
  border: 1px solid var(--warning-border-color);
  border-left-width: 4px;
  padding: var(--spacing-05);
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${Message} {
    width: 90%;
  }
  ${Control} {
    min-width: 100px;
  }
`;
const ContentItems = styled.ul``;

const ConfirmationContentWrapper = styled.div`
  ${Label01};
  color: var(--gray-80);

  ${ContentItems} {
    margin-bottom: var(--spacing-05);
  }
`;

const content = (
  headingText: string,
  items: string[],
  confirmationText: string,
) => (
  <ConfirmationContentWrapper>
    <span>{headingText}</span>
    <ContentItems>
      {items.map((item) => (
        <li>{item}</li>
      ))}
    </ContentItems>
    <span>{confirmationText}</span>
  </ConfirmationContentWrapper>
);

interface Props {
  enabled: boolean;
  onStateChange: (value: boolean) => void;
}

export const FeatureSwitch: FC<Props> = ({ enabled, onStateChange }) => {
  const disableButtonText = useTranslation('disable' as TranslationKey);
  const disableTitle = useTranslation(
    'settings.timesheets.disable_confirm.title' as TranslationKey,
  );
  const disableHeaderText = useTranslation(
    'settings.timesheets.disable_confirm.message_head' as TranslationKey,
  );
  const disableTextItems = [
    useTranslation(
      'settings.timesheets.disable_confirm.message_item_1' as TranslationKey,
    ),
    useTranslation(
      'settings.timesheets.disable_confirm.message_item_2' as TranslationKey,
    ),
  ];
  const disableConfirmationText = useTranslation(
    'settings.timesheets.disable_confirm.confirmation' as TranslationKey,
  );

  const onSwitchChange = (checked) => {
    if (checked) {
      onStateChange(checked);
    } else {
      confirm({
        okType: 'danger',
        okText: disableButtonText,
        title: disableTitle,
        closable: true,
        content: content(
          disableHeaderText,
          disableTextItems,
          disableConfirmationText,
        ),
        onOk: () => {
          onStateChange(checked);
        },
      });
    }
  };

  return (
    <Wrapper>
      <Message>
        <InfoIcon />
        <span>
          <FormattedMessage id="settings.timesheets.enable_message" />
        </span>
      </Message>
      <Control data-cy="switch-wrapper">
        <Switch
          size="small"
          checked={enabled}
          onChange={onSwitchChange}
          data-cy="switch.timesheet-feature"
        />
        {enabled ? (
          <FormattedMessage id="enabled" />
        ) : (
          <FormattedMessage id="disabled" />
        )}
      </Control>
    </Wrapper>
  );
};
